import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Tag, Space, message, Empty, Tooltip, Avatar, Badge, Spin, Modal } from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  DeleteOutlined, 
  MessageOutlined, 
  UserOutlined, 
  RobotOutlined, 
  CustomerServiceOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import apiClient from '../api';
import './ChatHistory.css';

const ChatHistory = () => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadChats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      scrollToBottom();
    }
  }, [messages, messagesLoading]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const loadChats = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/chat-history/all');
      if (response.data.success) {
        const formattedChats = (response.data.chats || []).map(chat => ({
          ...chat,
          lastMessage: chat.lastMessage || 'No messages',
          messageCount: chat.messageCount || 0,
          lastActivity: chat.lastActivity || null,
        }));
        setChats(formattedChats);
      }
    } catch (error) {
      message.error('Failed to load chat history');
      console.error('Error loading chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (chatId) => {
    setMessagesLoading(true);
    setSelectedChat(chatId);
    try {
      const response = await apiClient.get(`/chat-history/${chatId}?limit=200`);
      if (response.data.success) {
        setMessages(response.data.messages || []);
      }
    } catch (error) {
      message.error('Failed to load messages');
      console.error('Error loading messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const deleteChat = async (chatId) => {
    if (!chatId) return;
    try {
      await apiClient.delete(`/chat-history/${encodeURIComponent(chatId)}`);
      message.success('Chat history deleted');
      loadChats();
      setSelectedChat(null);
      setMessages([]);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete chat history');
      console.error('Error deleting chat:', error);
    }
  };

  const filteredChats = (chats || []).filter(chat => {
    const sText = (searchText || '').toLowerCase();
    const cId = (chat.chatId || '').toLowerCase();
    const lastMsg = (chat.lastMessage || '').toLowerCase();
    return cId.includes(sText) || lastMsg.includes(sText);
  });

  const getInitials = (id) => {
    if (!id) return '?';
    const clean = id.replace(/[^0-9a-zA-Z]/g, '');
    return clean.substring(0, 2).toUpperCase() || id.substring(0, 1).toUpperCase();
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    
    if (isToday) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="chat-history-container">
      {/* Header Section */}
      <div className="history-header-section">
        <div className="history-header-title">
          <h2><HistoryOutlined /> Interaction Log</h2>
          <p>Browse and review all customer conversations managed by AI and agents.</p>
        </div>
        <Space size="middle">
          <Input
            placeholder="Search by ID or message..."
            prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
            className="header-search"
          />
          <Button 
            type="primary"
            icon={<ReloadOutlined spin={loading} />} 
            onClick={loadChats}
            size="large"
            shape="round"
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Grid Section */}
      {loading && !chats.length ? (
        <div style={{ textAlign: 'center', padding: '100px' }}><Spin size="large" tip="Loading interactions..." /></div>
      ) : filteredChats.length === 0 ? (
        <div className="chat-empty-container">
          <Empty description={searchText ? "No matches found for your search" : "No conversation history available yet"} />
        </div>
      ) : (
        <div className="chat-grid">
          {filteredChats.map(chat => (
            <div 
              key={chat.chatId} 
              className="chat-card"
              onClick={() => loadMessages(chat.chatId)}
            >
              <div className="chat-card-avatar">
                 {getInitials(chat.chatId)}
              </div>
              
              <div className="chat-card-info">
                <div className="chat-card-id">{chat.chatId}</div>
                <div className="chat-card-time">
                  <ClockCircleOutlined /> {formatTime(chat.lastActivity)}
                </div>
              </div>

              <div className="chat-card-preview">
                {chat.lastMessage}
              </div>

              <div className="chat-card-footer">
                <div className="chat-card-badge">
                   <MessageOutlined /> {chat.messageCount}
                </div>
                <div className="chat-view-btn">
                  <ArrowRightOutlined />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History Modal */}
      <Modal
        title={null}
        open={!!selectedChat}
        onCancel={() => setSelectedChat(null)}
        footer={null}
        width={1000}
        centered
        className="chat-history-modal"
        styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '24px' } }}
      >
        <div className="modal-chat-container">
          <div style={{ padding: '24px 32px', background: 'white' }}>
            <div className="modal-chat-header">
              <Avatar size={50} style={{ backgroundColor: '#6366f1' }}>{getInitials(selectedChat)}</Avatar>
              <div style={{ flex: 1 }}>
                <div className="modal-chat-name">{selectedChat}</div>
                <div className="modal-chat-status">
                  <div className="status-pulse"></div>
                  SESSION LOG
                </div>
              </div>
              <Space>
                <Tooltip title="Clear History">
                  <Button 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => deleteChat(selectedChat)}
                    shape="circle"
                  />
                </Tooltip>
              </Space>
            </div>
          </div>

          <div className="modal-chat-messages">
            {messagesLoading ? (
              <div className="modal-loading">
                <Spin size="large" />
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty-container">
                <Empty description="No message logs found" />
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isUser = msg.role === 'user' || msg.role === 'system';
                const isHumanAgent = msg.metadata?.source === 'human_agent' || msg.agentId === 'human_agent';
                const role = isUser ? 'user' : isHumanAgent ? 'agent' : 'assistant';
                
                return (
                  <div key={`${msg.timestamp}-${idx}`} className={`message-bubble ${role}`}>
                    <div className="message-content">{msg.content}</div>
                    <div className="message-meta">
                      <span>{role === 'user' ? 'Client' : role === 'agent' ? 'Human Agent' : 'AI Assistant'}</span>
                      <span>{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChatHistory;
